import { useContext, useEffect, useState } from 'react'
import { MapMarker } from '../types'
import { BaseMarker, BaseMarkerProps } from './base-marker'
import { CustomMarker } from './custom-marker'
import { MapContext } from './map-context'

const NO_MARKERS: MapMarker<unknown>[] = []

export interface CustomMarkerLayerProps {
  markers?: MapMarker<unknown>[]
  componentType?: React.ElementType
  baseComponentProps?: Partial<BaseMarkerProps>
  classList?: string[]
  onMarkerClick?: (marker: MapMarker<unknown>) => void
}

/**
 * Layer used to display custom markers on the map. If a componentType is passed in, then the markers will use
 * the CustomMarker component and the componentProps will be the marker data itself. If componentType isn't
 * passed in, it will default to using the BaseMarker component. To pass props to BaseMarker, use
 * baseComponentProps. For example:
 *
 *    <CustomMarkerLayer markers={basicMarkers} baseComponentProps={{ markerColor: orange[500] }} />
 *
 * @param markers The list of marker data items to display.
 * @param componentType The React component type to use for the marker. Undefined if we are to use BaseMarker.
 * @param baseComponentProps The props to pass to BaseMarker if it's being used.
 *
 * @returns An element containing all the markers to be used on the map.
 */
export function CustomMarkerLayer({
  markers = NO_MARKERS,
  componentType,
  baseComponentProps,
  classList,
  onMarkerClick,
}: CustomMarkerLayerProps) {
  const { map, mapLoaded } = useContext(MapContext)
  const [markerElements, setMarkerElements] = useState<React.ReactNode[] | null>(null)

  useEffect(() => {
    if (!markers.length) {
      // Make sure to clear the elements or else they don't get removed from the map.
      setMarkerElements(null)
      return
    }

    if (map && mapLoaded) {
      const markerElements = markers.map((m, i) => {
        return (
          // If we have a componentType, use it to create a CustomMarker. Otherwise, use BaseMarker (which is
          // just a simple pin on the map).
          componentType ? (
            <CustomMarker
              map={map}
              componentType={componentType}
              componentProps={{ ...m }}
              coordinates={m.coordinate}
              classList={classList}
              onMarkerClick={onMarkerClick}
              key={`${m.coordinate.lat}-${m.coordinate.lng}-${i}`}
            />
          ) : (
            <BaseMarker
              map={map}
              coordinates={m.coordinate}
              {...baseComponentProps}
              key={`${m.coordinate.lat}-${m.coordinate.lng}-${i}`}
            />
          )
        )
      })

      setMarkerElements(markerElements)
    }
  }, [baseComponentProps, classList, componentType, map, mapLoaded, markers, onMarkerClick])

  return <div>{markerElements}</div>
}

export default CustomMarkerLayer
